import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type QueueValue = string;

interface RecordQueueContextValues {
  push: (value: QueueValue) => void;
  pop: (value: QueueValue) => void;
  clear: () => void;
  size: number;
}

const RecordQueueContext = createContext<RecordQueueContextValues | null>(null);

export function RecordQueueProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState(() => new Map<QueueValue, boolean>());

  const push = useCallback(
    (id: QueueValue) => {
      const newQueue = new Map(queue);
      newQueue.set(id, true);
      setQueue(newQueue);
    },
    [queue],
  );

  const pop = useCallback(
    (id: QueueValue) => {
      const newQueue = new Map(queue);
      newQueue.delete(id);
      setQueue(newQueue);
    },
    [queue],
  );

  const clear = useCallback(() => {
    setQueue(new Map());
  }, []);

  const size = useMemo(() => queue.size, [queue]);

  return <RecordQueueContext.Provider value={{ push, pop, clear, size }}>{children}</RecordQueueContext.Provider>;
}

export function useRecordQueue() {
  const context = useContext(RecordQueueContext);

  if (context === null) {
    throw new Error('useRecordModalContext must be used within a RecordModalProvider');
  }

  return context;
}
